import React, {useEffect, useState} from 'react';
import {Button, Col, Input, message, Modal, Row, Table} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import {PasswordControllerApi, PasswordDto} from "../api";
import {useKeycloakHeader} from "../config/Config";
import {useKeycloak} from "@react-keycloak/web";
import {hasRole, ROLES} from "../components/misc/RolesHelper";
import MissingAuthorizationPage from "./error/MissingAuthorizationPage";
import {getErrorMessage} from "../utils/Utils";
import PasswordCreationModal from "../components/modal/PasswordCreationModal";


const PasswordOverview = () => {
    const {keycloak} = useKeycloak();
    const [passwordDtos, setPasswordDtos] = useState<PasswordDto[]>();
    const [searchText, setSearchText] = useState<string>(''); // Der Suchbegriff
    const [modalVisible, setModalVisible] = useState(false);
    const [deletePasswordDto, setDeletePasswordDto] = useState<PasswordDto>();
    const [editPasswordDto, setEditPasswordDto] = useState<PasswordDto | null>(null);
    const [editPasswordModalVisible, setEditPasswordModalVisible] = useState(false);

    const headers = useKeycloakHeader();
    const passwordControllerApi = new PasswordControllerApi();

    const handleDeleteButton = (password: PasswordDto) => {
        setDeletePasswordDto(password);
        setModalVisible(true);
    }

    const onModalCancel = () => {
        setModalVisible(false);
        setEditPasswordDto(null);
    }

    const handleEditButton = (password: PasswordDto) => {
        setEditPasswordDto(password);
        setEditPasswordModalVisible(true);
    }

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Beschreibung',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Bearbeiten',
            key: 'edit',
            render: (text, record) => (
                <div>
                    {hasRole([ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_DEVELOPER], keycloak) ? (
                        <>
                            <Button
                                onClick={() => handleEditButton(record)}
                                style={{ marginRight: '8px', border: '0px' }}
                            >
                                <EditOutlined style={{ color: '#1890ff' }} />
                            </Button>
                            <Button
                                danger
                                onClick={() => handleDeleteButton(record)}
                                style={{ border: '0px' }}
                            >
                                <DeleteOutlined />
                            </Button>
                        </>
                    ) : (
                        <Button disabled style={{ marginRight: '8px', border: '0px' }}>
                            <DeleteOutlined />
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    const fetchData = () => {
        passwordControllerApi.getPasswords(headers)
            .then((axiosResponse) => {
                const axiosData: Array<PasswordDto> = axiosResponse.data;
                setPasswordDtos(axiosData);
            })
            .catch((error) => {
                let msg = "Fehler beim Abrufen der Daten: ";
                message.error(msg + getErrorMessage(error));
            });
    }

    useEffect(() => {
        fetchData();
    }, []);

    // Funktion zum Filtern der Tabelle basierend auf dem Suchbegriff
    const filteredData = passwordDtos?.filter((test) =>
        test.name?.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleDeletePassword = () => {
        if (deletePasswordDto && deletePasswordDto.id) {
            passwordControllerApi.deletePasswordById(deletePasswordDto.id, headers).then(response => {
                    // Erfolgreiche Antwort
                    message.success(deletePasswordDto.name + ' wurde erfolgreich gelöscht!');
                    setModalVisible(false);
                    fetchData();
                }
            ).catch((error) => {
                let msg = "Passwort konnte nicht gelöscht werden";
                message.error(msg + getErrorMessage(error));
            })
        }

    }

    const handlePasswordCreated = (value: boolean) => {
        if (value) {
            fetchData();
        }
    }

    const handleClosePasswordCreationModal = (value: boolean) => {
        if(value){
            setEditPasswordModalVisible(false);
            setEditPasswordDto(null);
        }
    }

    let roles = [
        ROLES.GLOW_ADMIN,
        ROLES.GLOW_TEST_DEVELOPER,
        ROLES.GLOW_TEST_DESIGNER,
        ROLES.GLOW_TEST_MANAGER
    ];
    const hasRoleForPage = hasRole(roles, keycloak);
    if (!hasRoleForPage) {
        return (<MissingAuthorizationPage roles={roles}/>);
    }
    return (
        <div>
            <h1>Passwörter</h1>
            <Row>
                {hasRole([ROLES.GLOW_ADMIN, ROLES.GLOW_TEST_DESIGNER], keycloak) &&
                    <Col flex={"70px"}>
                      <Button type="primary" onClick={() => setEditPasswordModalVisible(true)}>
                        <PlusOutlined/>
                      </Button>
                    </Col>}
                <Col flex={"auto"}>
                    <Input
                        placeholder="Nach Namen suchen"
                        onChange={(e) => setSearchText(e.target.value)}
                        value={searchText}
                    />
                </Col>
            </Row>
            <Table dataSource={filteredData} columns={columns} locale={{ emptyText: 'Keine Daten vorhanden' }}/>
            <Modal
                open={modalVisible}
                title={"Bist du dir sicher?"}
                onCancel={onModalCancel}
                footer={[
                    <Button key="cancel" onClick={onModalCancel}>
                        Nein
                    </Button>,
                    <Button key="save" danger onClick={handleDeletePassword}>
                        Ja
                    </Button>,
                ]}
            >
                <span>Möchtest du wirklich <b>{deletePasswordDto?.name} ({deletePasswordDto?.id})</b> löschen?</span>
            </Modal>
            <PasswordCreationModal visible={editPasswordModalVisible}
                                   onClose={handleClosePasswordCreationModal}
                                   onPasswordCreated={handlePasswordCreated}
                                   passwordToEdit={editPasswordDto}/>
        </div>
    );
};

export default PasswordOverview;